import React from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';

import type { InterventionCenterGovStatistics } from '../../../common/types/statistics/fhbfStatistics';
import faxForm from '../../../clients/detail/fhbfForm/fax/faxForm';
import formatter from '../../../common/helpers/formatter';
import policeMeasureForm from '../../../clients/detail/fhbfForm/interventionCenter/policeMeasureForm';
import contactForm from '../../../clients/detail/fhbfForm/interventionCenter/contactForm';
import anamnesisForm from '../../../clients/detail/fhbfForm/interventionCenter/anamnesisForm';
import OptionField from './components/OptionField';

type Props = {
  statistics: InterventionCenterGovStatistics;
};

function Case({ statistics }: Props) {
  const faxPoliceStationOptions = formatter.getFieldSelectOptions('policeStation', faxForm.fields);
  const policeMeasureOptions = formatter.getFieldSelectOptions('policeMeasure', policeMeasureForm.fields);
  const typeOfInitialContactOptions = formatter.getFieldSelectOptions('typeOfInitialContact', contactForm.fields);
  const ageOptions = formatter.getFieldSelectOptions('age', anamnesisForm.fields);
  const originOptions = formatter.getFieldSelectOptions('origin', anamnesisForm.fields);

  const numberOfCases = statistics.numberOfFaxes + statistics.numberOfSelfReporter;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'block',
        padding: '0px 24px 20px 24px',
        maxWidth: '650px',
        minWidth: '300px',
      }}
    >
      <Grid container rowSpacing={1}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2 Fälle gesamt
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            align='right'
            fontSize='0.9rem'
            fontWeight='bold'
            borderTop='2px solid black'
            borderRight='2px solid black'
            borderLeft='2px solid black'
            px='5px'
            sx={{ backgroundColor: '#BFBFBF' }}
          >
            {numberOfCases}
          </Typography>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <Typography fontSize='0.75rem' my={1}>
            <b>Hinweis:</b> Die Gesamtzahl der Fälle beinhaltet (Fax)übermittlungen durch die Polizei und
            Selbstmelderinnen!
          </Typography>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            Zahl der Datenübermittlungen durch die Polizei <span className='font-size-small'>(i.d.R. Faxe)</span>:
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            align='right'
            fontSize='0.9rem'
            fontWeight='bold'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            borderBottom='2px solid black'
            px='5px'
          >
            {statistics.numberOfFaxes}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>Polizeiinspektion</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem'>Anzahl</Typography>
        </Grid>
      </Grid>
      <Grid container pt={1} className='fhbf-print-text-border'>
        {faxPoliceStationOptions.map((option) => (
          <OptionField
            key={`faxesPerPoliceStation_${option.value}`}
            label={option.label}
            value={statistics.faxesPerPoliceStation[option.value]}
          />
        ))}
      </Grid>
      <Grid container rowSpacing={2} my={1}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' border='2px solid transparent' pl={1} sx={{ backgroundColor: '#BFBFBF' }}>
            Anzahl der Selbstmelderinnen:
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' border='2px solid black' align='right' px='5px'>
            {statistics.numberOfSelfReporter}
          </Typography>
        </Grid>
      </Grid>
      <div className='pagebreak'></div>
      <Grid container rowSpacing={1}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>davon Wiederholungsfälle bei der Interventionsstelle:</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' border='2px solid black' pr='5px'>
            {statistics.numberOfRepeatCases}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.1 Erfolgte Maßnahmen der Polizei:
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Soweit Maßnahme aus dem Fax erkennbar ist.
          </Typography>
        </Grid>
        <Grid container rowSpacing={2} mt={1} className='fhbf-print-text-value-border'>
          {policeMeasureOptions.map((option) => (
            <OptionField
              key={`policeMeasure_${option.value}`}
              label={option.label}
              value={statistics.policeMeasure[option.value]}
            />
          ))}
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.2 Art der <u>ersten</u> erfolgreichen Kontaktaufnahme mit der Frau
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Zählung der Kontaktaufnahme pro Frau = pro Fall.
          </Typography>
        </Grid>
        <Grid container rowSpacing={2} mt={1} className='fhbf-print-text-value-border'>
          {typeOfInitialContactOptions.map((option) => (
            <OptionField
              key={`typeOfInitialContact_${option.value}`}
              label={option.label}
              value={statistics.typeOfInitialContact[option.value]}
            />
          ))}
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Die geforderten maximalen drei Versuche der (telefonischen) Kontaktaufnahme und eine
            schriftliche/digitale werden als <b>eine</b> Kontaktaufnahme gezählt.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.3 Alter der von Gewalt betroffenen Frauen:
          </Typography>
        </Grid>
        <Grid container rowSpacing={2} mt={1} className='fhbf-print-text-value-border'>
          {ageOptions
            .filter((option) => option.value !== 'unknown')
            .map((option) => (
              <OptionField key={`age_${option.value}`} label={option.label} value={statistics.age[option.value]} />
            ))}
          {ageOptions
            .filter((option) => option.value === 'unknown')
            .map((option) => {
              const value = statistics.age[option.value] + (statistics.age['notSpecified'] || 0);
              return <OptionField key={`age_${option.value}`} label={option.label} value={value} />;
            })}
        </Grid>
      </Grid>
      <div className='pagebreak'></div>
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.4 Herkunft der von Gewalt betroffenen Frauen
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' mb={1}>
            Mit Migrationshintergrund (Definition: Migrantinnen der 1. Generation [selbst zugewandert], Migrantinnen der
            2. Generation [Eltern oder Elternteil zugewandert])
          </Typography>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Zählung soweit bekannt. Die Erläuterung "Definition Migrationshintergrund" dient zur
            Erklärung, sie bedeutet nicht, dass die jeweilige Herkunft immer zu erfragen ist.
          </Typography>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
        <Grid container rowSpacing={2} mt={1} className='fhbf-print-text-value-border'>
          {originOptions
            .filter((option) => option.value !== 'unknown')
            .map((option) => (
              <OptionField
                key={`origin_${option.value}`}
                label={option.label}
                value={statistics.origin[option.value]}
              />
            ))}
          {originOptions
            .filter((option) => option.value === 'unknown')
            .map((option) => {
              const value = statistics.origin[option.value] + (statistics.origin['notSpecified'] || 0);
              return <OptionField key={`origin_${option.value}`} label={option.label} value={value} />;
            })}
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.5 Anzahl der Frauen mit minderjährigen Kindern
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' border='2px solid black' px='5px' pb='25px'>
            {statistics.numberOfChildren}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.6 Anzahl der im Haushalt lebenden minderjährigen Kinder (soweit bekannt)
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>0 - 3 Jahre</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            pb='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.sumOfChildrenZeroToThreeYears}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>4 - 6 Jahre</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            pb='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.sumOfChildrenFourToSixYears}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>7 - 13 Jahre</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            pb='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.sumOfChildrenSevenToThirteenYears}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>14 - 17 Jahre</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' px='5px' pb='15px' border='2px solid black'>
            {statistics.sumOfChildrenFourteenToSeventeenYears}
          </Typography>
        </Grid>
      </Grid>
      <Grid container mt={2}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            2.7 Anzahl der schwangeren Frauen
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' border='2px solid black' px='5px' pb='15px'>
            {statistics.numberOfPregnancy}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default Case;
